// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import clearSearch from "@insite/client-framework/Store/Pages/OrderApprovalList/Handlers/ClearSearch";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderApprovalList/Handlers/UpdateSearchFields";
import updateShipTosSearchFields from "@insite/client-framework/Store/Pages/OrderApprovalList/Handlers/UpdateShipTosSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { xMask } from "@vanmeter/XDL/Utilities/mask";
import debounce from "lodash/debounce";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@vanmeter/MobiusOverrides/Link/Link";
import SetProductFilterText from "@vanmeter/App/Handlers/OrderHistory/SetProductFilterText";
import { mapSearchResultsToDropdownOptions } from "@vanmeter/App/Utilities/mapSearchResultsToDropdownOptions";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";

// XNG-Changes
enum fields {
    showTagsOnly = "showTagsOnly",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.showTagsOnly]: boolean;
    };
}

// FIX: Stable expand array defined outside mapStateToProps so its reference never changes.
// Previously `expand: ["approvals"]` was inline inside mapStateToProps, creating a new array
// instance on every render. getShipTosDataView looks up data by a serialized parameter key —
// if the key used for lookup differs from the key used when the response was stored, it returns
// undefined even when the API returned 200 with data. This was the cause of "No Options".
const SHIP_TO_EXPAND: string[] = ["approvals"];

const mapStateToProps = (state: CustomAppState) => {
    const { getShipTosParameter, getOrderApprovalsParameter, filtersOpen } = state.pages.orderApprovalList;
    const { searchResults, selectedProductInfo } = getProductSelector(state);
    const selectedProduct = getProductState(state, selectedProductInfo?.productId).value;

    // Reuse stable SHIP_TO_EXPAND so the parameter key matches what was stored in Redux.
    const getShipTosParameterExpanded = { ...getShipTosParameter, expand: SHIP_TO_EXPAND };
    return {
        filtersOpen,
        shipTosDataView:
            getShipTosParameterExpanded &&
            getShipTosDataView(state, getShipTosParameterExpanded as GetShipTosApiParameter),
        getOrderApprovalsParameter,
        getShipTosParameter,
        language: getSession(state).language,
        searchResults,
        selectedProduct,
        // XNG-Changes
        partNumberSearch: state.xProductFilterText.text,
        getShipTosParameterExpanded,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    clearSearch,
    updateShipTosSearchFields,
    loadShipTos,
    // XNG-Changes
    setProduct,
    searchProducts,
    reset,
    // XNG-Changes
    SetProductFilterText,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderApprovalListFilterStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    shipToGridItem?: GridItemProps;
    /** @deprecated Use the `shipToDropdown` property instead. */
    shipToSelect?: SelectPresentationProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberTextField?: TextFieldPresentationProps;
    datesGridItem?: GridItemProps;
    datesContainer?: GridContainerProps;
    dateRangeFieldset?: InjectableCss;
    fromGridItem?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    toGridItem?: GridItemProps;
    toDate?: DatePickerPresentationProps;
    orderTotalGridItem?: GridItemProps;
    orderTotalContainer?: GridContainerProps;
    orderTotalFieldset?: InjectableCss;
    orderTotalOperatorGridItem?: GridItemProps;
    orderTotalOperatorSelect?: SelectPresentationProps;
    orderTotalAmountGridItem?: GridItemProps;
    orderTotalAmountTextField?: TextFieldPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    buttonsItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;
    shipToDropdown?: DynamicDropdownPresentationProps;
    // XNG-Change
    clearLink?: LinkPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
}

export const filterStyles: OrderApprovalListFilterStyles = {
    container: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    heading: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    shipToGridItem: { width: [12, 12, 5, 5, 4] },
    orderNumberGridItem: { width: [12, 12, 3, 3, 2] },
    datesGridItem: { width: [12, 12, 6, 4, 4] },
    datesContainer: { gap: 10 },
    dateRangeFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    fromGridItem: { width: 6 },
    toGridItem: { width: 6 },
    appliedFiltersContainer: { css: horizontalStyles },
    buttonsItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    clearFiltersButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    orderTotalGridItem: { width: 12 },
    orderTotalContainer: { gap: 10 },
    orderTotalFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    orderTotalOperatorGridItem: { width: [6, 6, 3, 3, 2] },
    orderTotalAmountGridItem: { width: [6, 6, 3, 3, 2] },
    orderTotalAmountTextField: {
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 32px;
            `,
        },
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
};

const StyledDateRangeFieldSet = getStyledWrapper("fieldset");
const StyledOrderTotalFieldSet = getStyledWrapper("fieldset");

export const styles = filterStyles;

const OrderApprovalListFilter = ({
    filtersOpen,
    shipTosDataView,
    getShipTosParameter,
    getOrderApprovalsParameter,
    language,
    updateShipTosSearchFields,
    updateSearchFields,
    clearSearch,
    loadShipTos,
    // XNG-Changes
    fields,
    searchResults,
    setProduct,
    searchProducts,
    selectedProduct,
    reset,
    // XNG-Changes
    partNumberSearch,
    SetProductFilterText,
    getShipTosParameterExpanded,
}: Props) => {
    const [orderNumber, setOrderNumber] = useState(getOrderApprovalsParameter.orderNumber || "");
    const [orderTotal, setOrderTotal] = useState(getOrderApprovalsParameter.orderTotal || "");
    const [orderTotalOperator, setOrderTotalOperator] = useState(getOrderApprovalsParameter.orderTotalOperator || "");
    const [selectedShipToId, setSelectedShipToId] = useState(getOrderApprovalsParameter.shipToId || "");
    const [updateTimeoutId, setUpdateTimeoutId] = useState<NodeJS.Timeout | undefined>(undefined);
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [search, setSearch] = useState("");

    const [isAutoCompleteLoading, setIsAutoCompleteLoading] = useState(false);
    const isProductSelecting = React.useRef(false);
    const [partNumberOptions, setPartNumberOptions] = useState<OptionObject[]>([]);

    useEffect(() => {
        // FIX: Changed condition to handle null/undefined shipTosDataView on first render.
        // Previously `shipTosDataView && ...` would be falsy when shipTosDataView was null/undefined,
        // preventing loadShipTos from ever being called on initial mount.
        if (!fields.showTagsOnly && (!shipTosDataView || (!shipTosDataView.value && !shipTosDataView.isLoading))) {
            loadShipTos(getShipTosParameterExpanded);
        } else if (getShipTosParameterExpanded?.filter) {
            updateShipTosSearchFields({ ...getShipTosParameterExpanded, filter: undefined });
        }
    }, []);

    useEffect(() => {
        // FIX: Depend on shipTosDataView?.value instead of shipTosDataView object reference.
        // Redux may update internal properties without changing the object reference, so
        // depending on the whole object caused this effect to not re-run when data arrived,
        // leaving the dropdown showing only the default "Show All" / "Use Billing Address" entries.
        if (shipTosDataView?.value) {
            const newOptions: OptionObject[] = shipTosDataView.value.map(shipTo => ({
                optionText: shipTo.label,
                optionValue: shipTo.id,
            }));
            setOptions(newOptions);
        }
    }, [shipTosDataView?.value]);

    useEffect(() => {
        // FIX: Added !shipTosDataView?.value guard to prevent duplicate API calls when data
        // already exists. Previously this fired on every getShipTosParameter change regardless
        // of whether data had already been loaded, causing the duplicate calls visible in Network tab.
        if (!shipTosDataView?.isLoading && !shipTosDataView?.value && !fields.showTagsOnly) {
            loadShipTos(getShipTosParameterExpanded);
        }
    }, [getShipTosParameter]);

    useEffect(() => {
        setSearch("");
        debouncedUpdateSearchFields("");
    }, [getOrderApprovalsParameter]);

    useEffect(() => {
        if (shipTosDataView?.value) {
            const id = shipTosDataView.value.find(o => o.id === (getOrderApprovalsParameter.shipToId || ""))?.id;
            id && setSelectedShipToId(id);
        }
    }, [getOrderApprovalsParameter.shipToId]);

    useEffect(() => {
        updateParameterAfterTimeout();
    }, [orderNumber, orderTotal, orderTotalOperator]);

    // XNG-Changes:
    useEffect(() => {
        if ((getOrderApprovalsParameter as any).productErpNumber === undefined) {
            SetProductFilterText({ text: "" });
            setPartNumberOptions([]);
            reset();
        }
    }, [(getOrderApprovalsParameter as any).productErpNumber]);

    // XNG-Changes:
    useEffect(() => {
        setIsAutoCompleteLoading(false);
        if (!searchResults) {
            return;
        }

        const newOptions: OptionObject[] = mapSearchResultsToDropdownOptions(
            searchResults,
            "orderApprovalList_filterProduct",
        );
        setPartNumberOptions(newOptions);
    }, [searchResults]);

    // XNG-Changes:
    useEffect(() => {
        if (selectedProduct) {
            const erpNumber = selectedProduct.productNumber;
            updateSearchFields({ productErpNumber: erpNumber } as any);
            SetProductFilterText({ text: erpNumber });
            isProductSelecting.current = false;
        }
    }, [selectedProduct]);

    // XNG-Changes: update state value from parameter to fix clear filter issues
    useEffect(() => {
        setOrderNumber(getOrderApprovalsParameter.orderNumber || "");
        setOrderTotal(getOrderApprovalsParameter.orderTotal || "");
        setOrderTotalOperator(getOrderApprovalsParameter.orderTotalOperator || "");
        setSelectedShipToId(getOrderApprovalsParameter.shipToId || "");
    }, [
        getOrderApprovalsParameter.orderNumber,
        getOrderApprovalsParameter.orderTotal,
        getOrderApprovalsParameter.orderTotalOperator,
        getOrderApprovalsParameter.shipToId,
    ]);

    const updateParameterAfterTimeout = () => {
        clearTimeout(updateTimeoutId);
        setUpdateTimeoutId(
            setTimeout(() => {
                const setOrderTotalFilter = orderTotal && orderTotalOperator;
                updateSearchFields({
                    orderNumber,
                    orderTotalOperator: setOrderTotalFilter ? orderTotalOperator : "",
                    orderTotal: setOrderTotalFilter ? orderTotal : "",
                });
            }, 250),
        );
    };

    const selectShipToHandler = (id?: string) => {
        const shipToId = shipTosDataView?.value?.find(o => o.id === id)?.id;
        updateSearchFields({ shipToId });
    };

    const debouncedUpdateSearchFields = debounce((value: string) => {
        updateShipTosSearchFields({ ...getShipTosParameterExpanded, filter: value || undefined });
    }, 100);

    const changeSearchTextHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setSearch(value);
        debouncedUpdateSearchFields(value);
    };

    const shipToRemoveHandler = () => {
        setSelectedShipToId("");
        updateSearchFields({ shipToId: "" });
    };

    const orderNumberChangeHandler = (value: string) => {
        setOrderNumber(value);
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            fromDate: convertDateToDateOnlyString(selectedDay),
        });
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            toDate: convertDateToDateOnlyString(selectedDay),
        });
    };

    const orderTotalOperatorChangeHandler = (value: string) => {
        setOrderTotalOperator(value);
    };

    const orderTotalChangeHandler = (value: string) => {
        setOrderTotal(value);
    };

    // XNG-Changes
    const productErpNumberChangeHandler = (value: string) => {
        updateSearchFields({ productErpNumber: value } as any);
    };

    const clearFiltersClickHandler = () => {
        debouncedSearchProducts.cancel();
        setOrderNumber("");
        setOrderTotalOperator("");
        setOrderTotal("");
        setSelectedShipToId("");
        // XNG-Changes
        SetProductFilterText({ text: "" });
        clearSearch();
        // XNG-Change: Re-apply the default sort after clearing
        updateSearchFields({ sort: "orderDate" });
    };

    // XNG-Changes
    const onPartNumberInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isProductSelecting.current) {
            isProductSelecting.current = false;
            return;
        }
        setIsAutoCompleteLoading(true);
        debouncedSearchProducts(event.target.value);
    };

    const debouncedSearchProducts = React.useCallback(
        debounce((query: string) => {
            SetProductFilterText({ text: query });
            searchProducts({ query });
        }, 300),
        [],
    );

    // XNG-Changes: show search tags always if toggle is in open and closed status
    const searchTag = () => {
        // XNG-Changes: use orderNumber orderTotalOperator and orderTotal from the getOrderApprovalsParameter
        // to force them show in the tags
        const { fromDate, toDate, orderNumber, orderTotalOperator, orderTotal, productErpNumber } =
            getOrderApprovalsParameter as any;
        const selectedShipTo = options.find(o => o.optionValue === selectedShipToId);

        return (
            <StyledWrapper {...styles.appliedFiltersContainer}>
                {selectedShipTo && (
                    <Tag
                        {...styles.appliedFilterTag}
                        data-test-selector="orderApproval_shipToAddress_whenCollapsed"
                        onDelete={() => {
                            shipToRemoveHandler();
                        }}
                    >
                        {`${translate("Ship to Address")}: ${selectedShipTo.optionText}`}
                    </Tag>
                )}
                {orderNumber && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            orderNumberChangeHandler("");
                        }}
                    >
                        {`${translate("Order #")}: ${orderNumber}`}
                    </Tag>
                )}
                {fromDate && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            fromDateChangeHandler({});
                        }}
                    >
                        {`${translate("From")}: ${getLocalizedDateTime({
                            dateTime: convertDateOnlyStringToDate(fromDate)!,
                            language,
                        })}`}
                    </Tag>
                )}
                {toDate && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            toDateChangeHandler({});
                        }}
                    >
                        {`${translate("To")}: ${getLocalizedDateTime({
                            dateTime: convertDateOnlyStringToDate(toDate)!,
                            language,
                        })}`}
                    </Tag>
                )}
                {orderTotalOperator && orderTotal && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            orderTotalOperatorChangeHandler("");
                            orderTotalChangeHandler("");
                        }}
                    >
                        {`${translate("Order Total")}: ${orderTotalOperator} ${orderTotal}`}
                    </Tag>
                )}
                {/* XNG-Changes */}
                {productErpNumber && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            productErpNumberChangeHandler("");
                        }}
                    >
                        {`${translate("Part #")}: ${productErpNumber}`}
                    </Tag>
                )}
            </StyledWrapper>
        );
    };

    // XNG-Changes
    if (fields.showTagsOnly) {
        return <> {searchTag()} </>;
    }

    const fromDateObj = convertDateOnlyStringToDate(getOrderApprovalsParameter.fromDate);
    const toDateObj = convertDateOnlyStringToDate(getOrderApprovalsParameter.toDate);

    const onSelectionProductChangeHandler = (value?: string) => {
        if (searchResults && searchResults.length > 0) {
            isProductSelecting.current = true;
            const variantParentId = searchResults.find((o: any) => o.id === value)?.styleParentId;
            if (variantParentId) {
                setProduct({
                    productId: variantParentId,
                    variantId: value,
                    validateProduct: true,
                    skipInventoryValidation: true,
                });
            } else {
                setProduct({
                    productId: value,
                    validateProduct: true,
                    skipInventoryValidation: true,
                });
            }
        } else {
            updateSearchFields({ productErpNumber: value } as any);
        }
    };

    // XNG-Changes
    return (
        <GridContainer {...styles.container}>
            {/* <GridItem {...styles.headingGridItem}>
                            <Typography {...styles.heading} as="h2">
                                {translate("Filter")}
                            </Typography>
                        </GridItem> */}
            <GridItem {...styles.shipToGridItem}>
                <DynamicDropdown
                    {...styles.shipToDropdown}
                    label={translate("Ship To")}
                    onSelectionChange={selectShipToHandler}
                    onInputChange={changeSearchTextHandler}
                    selected={selectedShipToId}
                    isLoading={shipTosDataView?.isLoading}
                    options={options}
                    search={search}
                    data-test-selector="orderApproval_shipToAddress"
                    filterOption={() => true}
                />
            </GridItem>
            <GridItem {...styles.datesGridItem}>
                <StyledDateRangeFieldSet {...styles.dateRangeFieldset}>
                    <VisuallyHidden as="label">{translate("Date Range")}</VisuallyHidden>
                    <GridContainer {...styles.datesContainer}>
                        <GridItem data-test-selector="orderApproval_fromDateFilter" {...styles.fromGridItem}>
                            <DatePicker
                                {...styles.fromDate}
                                label={translate("From")}
                                selectedDay={fromDateObj}
                                onDayChange={fromDateChangeHandler}
                                dateTimePickerProps={{
                                    maxDate: toDateObj,
                                    ...styles.fromDate?.dateTimePickerProps,
                                }}
                            />
                        </GridItem>
                        <GridItem data-test-selector="orderApproval_toDateFilter" {...styles.toGridItem}>
                            <DatePicker
                                {...styles.toDate}
                                label={translate("To")}
                                selectedDay={toDateObj}
                                onDayChange={toDateChangeHandler}
                                dateTimePickerProps={{
                                    minDate: fromDateObj,
                                    ...styles.toDate?.dateTimePickerProps,
                                }}
                            />
                        </GridItem>
                    </GridContainer>
                </StyledDateRangeFieldSet>
            </GridItem>
            <GridItem {...styles.orderTotalGridItem}>
                <StyledOrderTotalFieldSet {...styles.orderTotalFieldset}>
                    <VisuallyHidden as="label">{translate("Order Total")}</VisuallyHidden>
                    <GridContainer {...styles.orderTotalContainer}>
                        <GridItem {...styles.orderTotalOperatorGridItem}>
                            <Select
                                {...styles.orderTotalOperatorSelect}
                                label={translate("Order Total")}
                                value={orderTotalOperator || ""}
                                data-test-selector="orderApproval_comparisonMethod"
                                onChange={event => orderTotalOperatorChangeHandler(event.target.value)}
                            >
                                <option value="">{translate("Select")}</option>
                                <option key="Greater Than" value="Greater Than">
                                    {translate("Greater Than")}
                                </option>
                                <option key="Less Than" value="Less Than">
                                    {translate("Less Than")}
                                </option>
                                <option key="Equal To" value="Equal To">
                                    {translate("Equal To")}
                                </option>
                            </Select>
                        </GridItem>
                        <GridItem {...styles.orderTotalAmountGridItem}>
                            <TextField
                                {...styles.orderTotalAmountTextField}
                                value={orderTotal || ""}
                                data-test-selector="orderApproval_comparisonValue"
                                onChange={event => {
                                    // XNG-Changes: prevent -/+ input into type number
                                    const value = xMask.decimal(event.target.value);
                                    orderTotalChangeHandler(value);
                                }}
                                // XNG-Change: type number
                                type="number"
                                min={0}
                                placeholder={translate("i.e. 6000")}
                                label={translate("Amount")}
                            />
                        </GridItem>
                    </GridContainer>
                </StyledOrderTotalFieldSet>
            </GridItem>

            {/* XNG-Changes: */}
            <GridItem {...styles.orderNumberGridItem}>
                <DynamicDropdown
                    {...styles.searchDynamicDropdown}
                    label={translate("Part #")}
                    onSelectionChange={onSelectionProductChangeHandler}
                    onInputChange={onPartNumberInputChanged}
                    filterOption={() => true}
                    selected={(getOrderApprovalsParameter as any).productErpNumber}
                    options={partNumberOptions}
                    hideNoOptionsIfEmptySearch
                    isLoading={isAutoCompleteLoading}
                    placeholder={translate("Part #")}
                    search={partNumberSearch}
                    data-test-selector="orderApproval_filterPart#"
                />
            </GridItem>

            {/* XNG-Change */}
            <GridItem {...styles.orderNumberGridItem}>
                <TextField
                    {...styles.orderNumberTextField}
                    value={orderNumber}
                    label={translate("Order #/PO #/Release #")}
                    data-test-selector="orderApproval_orderNumberFilter"
                    onChange={event => orderNumberChangeHandler(event.target.value)}
                    // XNG-Changes
                    placeholder={translate("Enter keyword or item #")}
                />
            </GridItem>

            <GridItem {...styles.buttonsItem}>
                {/* XNG-Changes */}
                <Link
                    {...styles.clearLink}
                    data-test-selector="orderApproval_clearFiltersButton"
                    onClick={clearFiltersClickHandler}
                    aria-label={translate("Clear Filters")}
                >
                    {translate("Clear Filters")}
                </Link>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderApprovalListFilter),
    definition: {
        group: "Order Approval List",
        displayName: "Search Results Filter",
        allowedContexts: ["OrderApprovalListPage"],
        isSystem: true,
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.showTagsOnly,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
